import { useState, useMemo } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { 
  Download, Filter, Phone, PhoneIncoming, PhoneOutgoing, 
  Clock, TrendingUp, Users, Activity, BarChart3, PieChart, 
  RefreshCw, ChevronDown, X, PhoneMissed,
  Timer, Percent, Zap, Shield, ArrowUpRight, ArrowDownRight, Hash
} from 'lucide-react';
import { 
  BarChart, Bar, PieChart as RechartsPie, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  AreaChart, Area
} from 'recharts';
import { format, subDays, startOfDay, endOfDay } from 'date-fns';
import { it } from 'date-fns/locale';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@easyflow/ui-frontend/components/ui/card';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@easyflow/ui-frontend/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@easyflow/ui-frontend/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@easyflow/ui-frontend/components/ui/table';
import { Popover, PopoverContent, PopoverTrigger } from '@easyflow/ui-frontend/components/ui/popover';
import { Separator } from '@easyflow/ui-frontend/components/ui/separator';
import { Progress } from '@easyflow/ui-frontend/components/ui/progress';
import api from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import CDRTable from '@/components/cdr/CDRTable';

interface AnalyticsFilters {
  startDate: Date;
  endDate: Date;
  storeId: string;
  trunkId: string;
  extensionId: string;
  direction: 'all' | 'inbound' | 'outbound' | 'internal';
  disposition: 'all' | 'answered' | 'no_answer' | 'busy' | 'failed' | 'cancelled' | 'rejected';
  durationMin: string;
  durationMax: string;
  callerIdSearch: string;
  didSearch: string;
}

const DISPOSITION_COLORS: Record<string, string> = {
  answered: '#22c55e',
  no_answer: '#f59e0b', 
  busy: '#f97316',
  failed: '#ef4444',
  cancelled: '#94a3b8',
  rejected: '#dc2626',
  other: '#6b7280',
  unknown: '#6b7280'
};

const DISPOSITION_LABELS: Record<string, string> = {
  answered: 'Risposte',
  no_answer: 'Senza risposta',
  busy: 'Occupato',
  failed: 'Fallite',
  cancelled: 'Annullate',
  rejected: 'Rifiutate',
  other: 'Altro',
  unknown: 'Sconosciuto'
};

const DIRECTION_COLORS: Record<string, string> = {
  inbound: '#22c55e',
  outbound: '#3b82f6',
  internal: '#8b5cf6'
};

interface StoreOption {
  id: string;
  name?: string;
  [key: string]: unknown;
}

interface TrunkOption {
  id: string;
  name?: string;
  gateway?: string;
  did_config?: { main_number?: string };
  [key: string]: unknown;
}

interface ExtensionOption {
  id: string;
  extension: string;
  display_name?: string;
  caller_id_name?: string;
  [key: string]: unknown;
}

interface AnalyticsOverview {
  total_calls?: number | string;
  asr?: number | string;
  acd?: number | string;
  missed_calls?: number | string;
  total_duration?: number | string;
  active_extensions?: number | string;
  sla_20s?: number | string;
  peak_hour?: { hour?: number | string; count?: number | string } | null;
  dispositions?: Record<string, number | string>;
  directions?: Record<string, number | string>;
  top_callers?: Array<{ number: string; name?: string; calls?: number | string; total_duration?: number | string }>;
  top_destinations?: Array<{ number: string; calls?: number | string; total_duration?: number | string }>;
  [key: string]: unknown;
}

interface TrafficPoint {
  date: string;
  inbound?: number | string;
  outbound?: number | string;
  internal?: number | string;
  [key: string]: unknown;
}

interface HeatmapPoint {
  day: number | string;
  hour: number | string;
  count?: number | string;
  [key: string]: unknown;
}

interface ExtensionStat {
  extension: string;
  display_name?: string;
  total_calls?: number | string;
  answered?: number | string;
  missed?: number | string;
  answer_rate?: number | string;
  avg_duration?: number | string;
  total_duration?: number | string;
  [key: string]: unknown;
}

interface TrunkStat {
  trunk_id?: string;
  trunk_name?: string;
  trunk_did?: string;
  total_calls?: number | string;
  inbound?: number | string;
  outbound?: number | string;
  internal?: number | string;
  failed?: number | string;
  fail_rate?: number | string;
  asr?: number | string;
  avg_duration?: number | string;
  total_duration?: number | string;
  [key: string]: unknown;
}

export default function Analytics() {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('overview');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  
  const [filters, setFilters] = useState<AnalyticsFilters>({
    startDate: subDays(new Date(), 30),
    endDate: new Date(),
    storeId: 'all',
    trunkId: 'all',
    extensionId: 'all',
    direction: 'all',
    disposition: 'all',
    durationMin: '',
    durationMax: '',
    callerIdSearch: '',
    didSearch: ''
  });

  const { data: storesData = [] } = useQuery<StoreOption[]>({
    queryKey: ['stores'],
    queryFn: async () => {
      const res = await api.get<{ items?: StoreOption[]; data?: StoreOption[] } & StoreOption[]>('/stores');
      const data = res.data?.items || res.data?.data || res.data;
      return Array.isArray(data) ? data : [];
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
  const stores = Array.isArray(storesData) ? storesData : [];

  const { data: trunksData = [] } = useQuery<TrunkOption[]>({
    queryKey: ['trunks'],
    queryFn: async () => {
      const res = await api.get<{ items?: TrunkOption[]; data?: TrunkOption[] } & TrunkOption[]>('/sip-trunks');
      const data = res.data?.items || res.data?.data || res.data;
      return Array.isArray(data) ? data : [];
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
  const trunks = Array.isArray(trunksData) ? trunksData : [];

  const { data: extensionsData = [] } = useQuery<ExtensionOption[]>({
    queryKey: ['extensions'],
    queryFn: async () => {
      const res = await api.get<{ items?: ExtensionOption[]; data?: ExtensionOption[] } & ExtensionOption[]>('/extensions');
      const data = res.data?.items || res.data?.data || res.data;
      return Array.isArray(data) ? data : [];
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
  const extensions = Array.isArray(extensionsData) ? extensionsData : [];

  const filtersQuery = useMemo(() => {
    return {
      start_date: format(filters.startDate, 'yyyy-MM-dd'),
      end_date: format(filters.endDate, 'yyyy-MM-dd'),
      trunk_id: filters.trunkId !== 'all' ? filters.trunkId : undefined,
      store_id: filters.storeId !== 'all' ? filters.storeId : undefined,
      extension: filters.extensionId !== 'all' ? filters.extensionId : undefined,
      direction: filters.direction !== 'all' ? filters.direction : undefined,
      disposition: filters.disposition !== 'all' ? filters.disposition : undefined,
      duration_min: filters.durationMin || undefined,
      duration_max: filters.durationMax || undefined,
      caller_id: filters.callerIdSearch || undefined,
      did: filters.didSearch || undefined
    };
  }, [filters]);

  const buildParams = (extra?: Record<string, string>) => {
    const params = new URLSearchParams();
    Object.entries({ ...filtersQuery, ...extra }).forEach(([k, v]) => v && params.set(k, String(v)));
    return params;
  };

  const { data: overview, refetch: refetchOverview } = useQuery<AnalyticsOverview>({
    queryKey: ['analytics-overview', filtersQuery],
    queryFn: async () => {
      const res = await api.get<{ data?: AnalyticsOverview } & AnalyticsOverview>(`/analytics/overview?${buildParams()}`);
      return res.data?.data || res.data || {};
    },
    staleTime: 30 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchInterval: autoRefresh ? 30000 : false,
    placeholderData: keepPreviousData,
  });

  const { data: trafficData = [] } = useQuery<TrafficPoint[]>({
    queryKey: ['analytics-traffic', filtersQuery],
    queryFn: async () => {
      const res = await api.get<{ data?: TrafficPoint[] } & TrafficPoint[]>(`/analytics/traffic?${buildParams()}`);
      return res.data?.data || res.data || [];
    },
    staleTime: 60 * 1000,
    gcTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const { data: heatmapData = [] } = useQuery<HeatmapPoint[]>({
    queryKey: ['analytics-heatmap', filtersQuery],
    queryFn: async () => {
      const res = await api.get<{ data?: HeatmapPoint[] } & HeatmapPoint[]>(`/analytics/heatmap?${buildParams()}`);
      return res.data?.data || res.data || [];
    },
    staleTime: 60 * 1000,
    gcTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const { data: extensionsStats = [] } = useQuery<ExtensionStat[]>({
    queryKey: ['analytics-extensions', filtersQuery],
    queryFn: async () => {
      const res = await api.get<{ data?: ExtensionStat[] } & ExtensionStat[]>(`/analytics/extensions?${buildParams()}`);
      return res.data?.data || res.data || [];
    },
    staleTime: 60 * 1000,
    gcTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const { data: trunksStats = [] } = useQuery<TrunkStat[]>({
    queryKey: ['analytics-trunks', filtersQuery],
    queryFn: async () => {
      const res = await api.get<{ data?: TrunkStat[] } & TrunkStat[]>(`/analytics/trunks?${buildParams()}`);
      return res.data?.data || res.data || [];
    },
    staleTime: 60 * 1000,
    gcTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const resetFilters = () => {
    setFilters({
      startDate: subDays(new Date(), 7),
      endDate: new Date(),
      storeId: 'all',
      trunkId: 'all',
      extensionId: 'all',
      direction: 'all',
      disposition: 'all',
      durationMin: '',
      durationMax: '',
      callerIdSearch: '',
      didSearch: ''
    });
  };

  const handleExport = async (exportFormat: 'csv' | 'xlsx' | 'json') => {
    try {
      setIsExporting(true);
      const params = buildParams({ format: exportFormat });
      
      let blob: Blob;
      if (exportFormat === 'json') {
        const res = await api.get<unknown>(`/analytics/export?${params}`);
        const jsonPayload = res.data ?? res;
        blob = new Blob([JSON.stringify(jsonPayload, null, 2)], { type: 'application/json' });
      } else {
        // Task #3718 — download binario reale (CSV/XLSX) via getBlob:
        // api.get() ignorava il secondo argomento e salvava l'envelope JSON.
        blob = await api.getBlob(`/analytics/export?${params}`);
      }
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `analytics_${format(new Date(), 'yyyy-MM-dd_HHmm')}.${exportFormat === 'xlsx' ? 'csv' : exportFormat}`;
      a.click();
      window.URL.revokeObjectURL(url);
      
      toast({ title: 'Export completato', description: `Dati esportati in ${exportFormat.toUpperCase()}` });
    } catch (error) {
      toast({ title: 'Errore export', description: 'Impossibile esportare i dati', variant: 'destructive' });
    } finally {
      setIsExporting(false);
    }
  };

  const formatDuration = (seconds: number | string | null | undefined) => {
    if (!seconds) return '0:00';
    const s = Math.round(Number(seconds));
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    if (h > 0) return `${h}:${m.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`;
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  const dispositionData = useMemo(() => {
    if (!overview?.dispositions) return [];
    return Object.entries(overview.dispositions)
      .filter(([_, value]) => (value as number) > 0)
      .map(([name, value]) => ({
        name: DISPOSITION_LABELS[name] || name,
        value: value as number,
        color: DISPOSITION_COLORS[name] || DISPOSITION_COLORS.unknown
      }))
      .sort((a, b) => b.value - a.value);
  }, [overview]);

  const directionData = useMemo(() => {
    if (!overview?.directions) return [];
    const labels: Record<string, string> = { inbound: 'Entrata', outbound: 'Uscita', internal: 'Interno' };
    return Object.entries(overview.directions).map(([name, value]) => ({
      name: labels[name] || name,
      value: value as number,
      color: DIRECTION_COLORS[name] || '#6b7280'
    }));
  }, [overview]);

  const presetRanges = [
    { label: 'Oggi', days: 0 },
    { label: 'Ieri', days: 1 },
    { label: '7 giorni', days: 7 },
    { label: '30 giorni', days: 30 },
    { label: '90 giorni', days: 90 }
  ];

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Analytics</h1>
          <p className="text-muted-foreground">Analisi dettagliata delle chiamate e performance</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 border rounded-md overflow-hidden">
            {presetRanges.map(p => (
              <Button 
                key={p.label} 
                variant="ghost" 
                size="sm"
                className="rounded-none text-xs px-2"
                onClick={() => setFilters(f => ({
                  ...f,
                  startDate: p.days === 0 ? startOfDay(new Date()) : p.days === 1 ? startOfDay(subDays(new Date(), 1)) : subDays(new Date(), p.days),
                  endDate: p.days === 1 ? endOfDay(subDays(new Date(), 1)) : new Date()
                }))}
              >
                {p.label}
              </Button>
            ))}
          </div>
          <Button
            variant={autoRefresh ? "default" : "outline"}
            size="sm"
            onClick={() => setAutoRefresh(!autoRefresh)}
          >
            <RefreshCw className={cn("h-4 w-4 mr-1", autoRefresh && "animate-spin")} />
            {autoRefresh ? 'Auto ON' : 'Auto OFF'}
          </Button>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" disabled={isExporting}>
                <Download className="h-4 w-4 mr-1" />
                Esporta
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-40" align="end">
              <div className="flex flex-col gap-1">
                <Button variant="ghost" size="sm" onClick={() => handleExport('csv')}>CSV</Button>
                <Button variant="ghost" size="sm" onClick={() => handleExport('json')}>JSON</Button>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      <Card className="backdrop-blur-sm bg-card/80">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Filter className="h-5 w-5" /> Filtri
            </CardTitle>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={resetFilters}>
                <X className="h-4 w-4 mr-1" /> Reset
              </Button>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              >
                <ChevronDown className={cn("h-4 w-4 mr-1 transition-transform", showAdvancedFilters && "rotate-180")} />
                {showAdvancedFilters ? 'Nascondi' : 'Avanzati'}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
            <div>
              <Label className="text-xs text-muted-foreground">Dal</Label>
              <Input
                type="date"
                value={format(filters.startDate, 'yyyy-MM-dd')}
                onChange={(e) => e.target.value && setFilters(f => ({ ...f, startDate: startOfDay(new Date(e.target.value)) }))}
                className="h-10"
              />
            </div>

            <div>
              <Label className="text-xs text-muted-foreground">Al</Label>
              <Input
                type="date"
                value={format(filters.endDate, 'yyyy-MM-dd')}
                onChange={(e) => e.target.value && setFilters(f => ({ ...f, endDate: endOfDay(new Date(e.target.value)) }))}
                className="h-10"
              />
            </div>

            <div>
              <Label className="text-xs text-muted-foreground">Sede</Label>
              <Select value={filters.storeId} onValueChange={(v) => setFilters(f => ({ ...f, storeId: v }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Tutte le sedi" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutte le sedi</SelectItem>
                  {stores.map((store) => (
                    <SelectItem key={store.id} value={store.id}>{store.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs text-muted-foreground">Trunk</Label>
              <Select value={filters.trunkId} onValueChange={(v) => setFilters(f => ({ ...f, trunkId: v }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Tutti i trunk" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti i trunk</SelectItem>
                  {trunks.map((trunk) => (
                    <SelectItem key={trunk.id} value={trunk.id}>
                      {trunk.name || trunk.gateway} {trunk.did_config?.main_number ? `• ${trunk.did_config.main_number}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs text-muted-foreground">Stato</Label>
              <Select value={filters.disposition} onValueChange={(v) => setFilters(f => ({ ...f, disposition: v as AnalyticsFilters['disposition'] }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Tutti" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti gli stati</SelectItem>
                  <SelectItem value="answered">Risposta</SelectItem>
                  <SelectItem value="no_answer">Senza risposta</SelectItem>
                  <SelectItem value="cancelled">Annullata</SelectItem>
                  <SelectItem value="rejected">Rifiutata</SelectItem>
                  <SelectItem value="busy">Occupato</SelectItem>
                  <SelectItem value="failed">Fallita</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs text-muted-foreground">Estensione</Label>
              <Select value={filters.extensionId} onValueChange={(v) => setFilters(f => ({ ...f, extensionId: v }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Tutte" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutte le estensioni</SelectItem>
                  {extensions.map((ext) => (
                    <SelectItem key={ext.id} value={ext.extension}>{ext.extension} - {ext.display_name || ext.caller_id_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs text-muted-foreground">Direzione</Label>
              <Select value={filters.direction} onValueChange={(v) => setFilters(f => ({ ...f, direction: v as AnalyticsFilters['direction'] }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutte</SelectItem>
                  <SelectItem value="inbound">Entrata</SelectItem>
                  <SelectItem value="outbound">Uscita</SelectItem>
                  <SelectItem value="internal">Interno</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {showAdvancedFilters && (
            <>
              <Separator />
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
                <div>
                  <Label className="text-xs text-muted-foreground">Durata min (sec)</Label>
                  <Input 
                    type="number" 
                    placeholder="0"
                    value={filters.durationMin}
                    onChange={(e) => setFilters(f => ({ ...f, durationMin: e.target.value }))}
                  />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Durata max (sec)</Label>
                  <Input 
                    type="number" 
                    placeholder="∞"
                    value={filters.durationMax}
                    onChange={(e) => setFilters(f => ({ ...f, durationMax: e.target.value }))}
                  />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Caller ID</Label>
                  <Input 
                    placeholder="Cerca numero..."
                    value={filters.callerIdSearch}
                    onChange={(e) => setFilters(f => ({ ...f, callerIdSearch: e.target.value }))}
                  />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">DID</Label>
                  <Input 
                    placeholder="Cerca DID..."
                    value={filters.didSearch}
                    onChange={(e) => setFilters(f => ({ ...f, didSearch: e.target.value }))}
                  />
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
        <Card className="backdrop-blur-sm bg-card/80">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Chiamate</p>
                <p className="text-2xl font-bold">{overview?.total_calls?.toLocaleString() || 0}</p>
              </div>
              <Phone className="h-6 w-6 text-primary opacity-40" />
            </div>
          </CardContent>
        </Card>

        <Card className="backdrop-blur-sm bg-card/80">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">ASR</p>
                <p className="text-2xl font-bold text-green-600">{Number(overview?.asr || 0).toFixed(1)}%</p>
              </div>
              <Percent className="h-6 w-6 text-green-600 opacity-40" />
            </div>
          </CardContent>
        </Card>

        <Card className="backdrop-blur-sm bg-card/80">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">ACD</p>
                <p className="text-2xl font-bold">{formatDuration(overview?.acd || 0)}</p>
              </div>
              <Timer className="h-6 w-6 text-blue-600 opacity-40" />
            </div>
          </CardContent>
        </Card>

        <Card className="backdrop-blur-sm bg-card/80">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Perse</p>
                <p className="text-2xl font-bold text-red-600">{overview?.missed_calls?.toLocaleString() || 0}</p>
              </div>
              <PhoneMissed className="h-6 w-6 text-red-600 opacity-40" />
            </div>
          </CardContent>
        </Card>

        <Card className="backdrop-blur-sm bg-card/80">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Tempo Totale</p>
                <p className="text-2xl font-bold">{formatDuration(overview?.total_duration || 0)}</p>
              </div>
              <Clock className="h-6 w-6 text-purple-600 opacity-40" />
            </div>
          </CardContent>
        </Card>

        <Card className="backdrop-blur-sm bg-card/80">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Interni Attivi</p>
                <p className="text-2xl font-bold">{overview?.active_extensions || 0}</p>
              </div>
              <Users className="h-6 w-6 text-orange-600 opacity-40" />
            </div>
          </CardContent>
        </Card>

        <Card className="backdrop-blur-sm bg-card/80">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">SLA 20s</p>
                <p className={cn("text-2xl font-bold", Number(overview?.sla_20s || 0) >= 80 ? 'text-green-600' : Number(overview?.sla_20s || 0) >= 60 ? 'text-yellow-600' : 'text-red-600')}>
                  {Number(overview?.sla_20s || 0).toFixed(1)}%
                </p>
              </div>
              <Shield className="h-6 w-6 text-indigo-600 opacity-40" />
            </div>
          </CardContent>
        </Card>

        <Card className="backdrop-blur-sm bg-card/80">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Ora di Punta</p>
                <p className="text-2xl font-bold">
                  {overview?.peak_hour ? `${overview.peak_hour.hour}:00` : '-'}
                </p>
              </div>
              <Zap className="h-6 w-6 text-amber-500 opacity-40" />
            </div>
            {overview?.peak_hour && (
              <p className="text-xs text-muted-foreground mt-1">{overview.peak_hour.count} chiamate</p>
            )}
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex w-full">
          <TabsTrigger value="overview" className="flex-1 flex items-center justify-center gap-2">
            <BarChart3 className="h-4 w-4" /> Panoramica
          </TabsTrigger>
          <TabsTrigger value="traffic" className="flex-1 flex items-center justify-center gap-2">
            <TrendingUp className="h-4 w-4" /> Traffico
          </TabsTrigger>
          <TabsTrigger value="extensions" className="flex-1 flex items-center justify-center gap-2">
            <Users className="h-4 w-4" /> Estensioni
          </TabsTrigger>
          <TabsTrigger value="trunks" className="flex-1 flex items-center justify-center gap-2">
            <Activity className="h-4 w-4" /> Trunks
          </TabsTrigger>
          <TabsTrigger value="cdr" className="flex-1 flex items-center justify-center gap-2">
            <Phone className="h-4 w-4" /> CDR Dettaglio
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6 mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="backdrop-blur-sm bg-card/80">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <PieChart className="h-5 w-5" /> Esito Chiamate
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%" minHeight={300}>
                    <RechartsPie>
                      <Pie
                        data={dispositionData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={2}
                        dataKey="value"
                        label={({ name, percent }: { name?: string; percent?: number }) => `${name} ${((percent || 0) * 100).toFixed(0)}%`}
                      >
                        {dispositionData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </RechartsPie>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card className="backdrop-blur-sm bg-card/80">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" /> Direzione Chiamate
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%" minHeight={300}>
                    <BarChart data={directionData}>
                      <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                        {directionData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="backdrop-blur-sm bg-card/80">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <PhoneIncoming className="h-5 w-5" /> Top 10 Chiamanti
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>#</TableHead>
                        <TableHead>Numero</TableHead>
                        <TableHead>Nome</TableHead>
                        <TableHead className="text-right">Chiamate</TableHead>
                        <TableHead className="text-right">Durata</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(overview?.top_callers || []).map((c, i: number) => (
                        <TableRow key={c.number}>
                          <TableCell className="text-muted-foreground">{i + 1}</TableCell>
                          <TableCell className="font-mono">{c.number}</TableCell>
                          <TableCell className="text-muted-foreground">{c.name || '-'}</TableCell>
                          <TableCell className="text-right font-medium">{c.calls}</TableCell>
                          <TableCell className="text-right text-muted-foreground">{formatDuration(c.total_duration)}</TableCell>
                        </TableRow>
                      ))}
                      {(!overview?.top_callers || overview.top_callers.length === 0) && (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center text-muted-foreground py-6">Nessun dato</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>

            <Card className="backdrop-blur-sm bg-card/80">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <PhoneOutgoing className="h-5 w-5" /> Top 10 Destinazioni
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>#</TableHead>
                        <TableHead>Numero</TableHead>
                        <TableHead className="text-right">Chiamate</TableHead>
                        <TableHead className="text-right">Durata</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(overview?.top_destinations || []).map((d, i: number) => (
                        <TableRow key={d.number}>
                          <TableCell className="text-muted-foreground">{i + 1}</TableCell>
                          <TableCell className="font-mono">{d.number}</TableCell>
                          <TableCell className="text-right font-medium">{d.calls}</TableCell>
                          <TableCell className="text-right text-muted-foreground">{formatDuration(d.total_duration)}</TableCell>
                        </TableRow>
                      ))}
                      {(!overview?.top_destinations || overview.top_destinations.length === 0) && (
                        <TableRow>
                          <TableCell colSpan={4} className="text-center text-muted-foreground py-6">Nessun dato</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card className="backdrop-blur-sm bg-card/80">
            <CardHeader>
              <CardTitle>Heatmap Oraria</CardTitle>
              <CardDescription>Distribuzione chiamate per ora e giorno della settimana</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <div className="min-w-[600px]">
                  <div className="grid grid-cols-[auto_repeat(24,1fr)] gap-1 text-xs">
                    <div></div>
                    {Array.from({ length: 24 }, (_, i) => (
                      <div key={i} className="text-center text-muted-foreground">{i}</div>
                    ))}
                    {['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'].map((day, dayIndex) => (
                      <div key={`row-${day}`} className="contents">
                        <div className="text-muted-foreground pr-2 flex items-center">{day}</div>
                        {Array.from({ length: 24 }, (_, hour) => {
                          const value = Number(heatmapData.find((h) => Number(h.day) === dayIndex && Number(h.hour) === hour)?.count || 0);
                          const maxValue = Math.max(...heatmapData.map((h) => Number(h.count || 0)), 1);
                          const intensity = value / maxValue;
                          return (
                            <div
                              key={`${day}-${hour}`}
                              className="aspect-square rounded-sm cursor-default"
                              style={{
                                backgroundColor: value > 0 
                                  ? `rgba(59, 130, 246, ${Math.max(0.1, intensity)})` 
                                  : 'rgba(0,0,0,0.05)',
                              }}
                              title={`${day} ${hour}:00 - ${value} chiamate`}
                            />
                          );
                        })}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="traffic" className="space-y-6 mt-6">
          <Card className="backdrop-blur-sm bg-card/80">
            <CardHeader>
              <CardTitle>Andamento Traffico</CardTitle>
              <CardDescription>Volume chiamate nel periodo selezionato</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[400px]">
                <ResponsiveContainer width="100%" height="100%" minHeight={400}>
                  <AreaChart data={trafficData}>
                    <defs>
                      <linearGradient id="colorInbound" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#22c55e" stopOpacity={0}/>
                      </linearGradient>
                      <linearGradient id="colorOutbound" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                      </linearGradient>
                      <linearGradient id="colorInternal" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                    <XAxis dataKey="date" tickFormatter={(v) => { try { return format(new Date(v), 'dd/MM'); } catch { return v; }}} />
                    <YAxis />
                    <Tooltip labelFormatter={(v) => { const s = typeof v === 'string' || typeof v === 'number' ? v : String(v ?? ''); try { return format(new Date(s), 'dd/MM/yyyy'); } catch { return s; }}} />
                    <Legend />
                    <Area type="monotone" dataKey="inbound" name="Entrata" stroke="#22c55e" fillOpacity={1} fill="url(#colorInbound)" />
                    <Area type="monotone" dataKey="outbound" name="Uscita" stroke="#3b82f6" fillOpacity={1} fill="url(#colorOutbound)" />
                    <Area type="monotone" dataKey="internal" name="Interno" stroke="#8b5cf6" fillOpacity={1} fill="url(#colorInternal)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="extensions" className="space-y-6 mt-6">
          <Card className="backdrop-blur-sm bg-card/80">
            <CardHeader>
              <CardTitle>Performance Estensioni</CardTitle>
              <CardDescription>Statistiche per singola estensione</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Estensione</TableHead>
                      <TableHead>Nome</TableHead>
                      <TableHead className="text-right">Chiamate</TableHead>
                      <TableHead className="text-right">Risposte</TableHead>
                      <TableHead className="text-right">Perse</TableHead>
                      <TableHead className="text-right">Answer Rate</TableHead>
                      <TableHead className="text-right">Durata Media</TableHead>
                      <TableHead className="text-right">Tempo Totale</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {extensionsStats.map((ext) => (
                      <TableRow key={ext.extension}>
                        <TableCell className="font-mono font-medium">{ext.extension}</TableCell>
                        <TableCell>{ext.display_name || '-'}</TableCell>
                        <TableCell className="text-right">{ext.total_calls}</TableCell>
                        <TableCell className="text-right text-green-600">{ext.answered}</TableCell>
                        <TableCell className="text-right text-red-600">{ext.missed}</TableCell>
                        <TableCell className="text-right">
                          <Badge variant={Number(ext.answer_rate || 0) >= 80 ? 'default' : Number(ext.answer_rate || 0) >= 50 ? 'secondary' : 'destructive'}>
                            {Number(ext.answer_rate || 0).toFixed(1)}%
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">{formatDuration(ext.avg_duration || 0)}</TableCell>
                        <TableCell className="text-right">{formatDuration(ext.total_duration || 0)}</TableCell>
                      </TableRow>
                    ))}
                    {extensionsStats.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                          Nessun dato disponibile per il periodo selezionato
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="trunks" className="space-y-6 mt-6">
          <Card className="backdrop-blur-sm bg-card/80">
            <CardHeader>
              <CardTitle>Utilizzo Trunk SIP</CardTitle>
              <CardDescription>Performance e statistiche per trunk con risoluzione intelligente</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Trunk</TableHead>
                      <TableHead>DID</TableHead>
                      <TableHead className="text-right">Chiamate</TableHead>
                      <TableHead className="text-right">Entrata</TableHead>
                      <TableHead className="text-right">Uscita</TableHead>
                      <TableHead className="text-right">Interne</TableHead>
                      <TableHead className="text-right">Fallite</TableHead>
                      <TableHead className="text-right">Fail Rate</TableHead>
                      <TableHead className="text-right">ASR</TableHead>
                      <TableHead className="text-right">ACD</TableHead>
                      <TableHead className="text-right">Minuti</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {trunksStats.map((trunk, idx: number) => (
                      <TableRow key={trunk.trunk_id || idx}>
                        <TableCell className="font-medium">{trunk.trunk_name}</TableCell>
                        <TableCell className="text-muted-foreground font-mono text-xs">{trunk.trunk_did || '-'}</TableCell>
                        <TableCell className="text-right font-bold">{trunk.total_calls}</TableCell>
                        <TableCell className="text-right text-green-600">{trunk.inbound}</TableCell>
                        <TableCell className="text-right text-blue-600">{trunk.outbound}</TableCell>
                        <TableCell className="text-right text-purple-600">{trunk.internal || 0}</TableCell>
                        <TableCell className="text-right text-red-600">{trunk.failed}</TableCell>
                        <TableCell className="text-right">
                          <Badge variant={Number(trunk.fail_rate || 0) <= 5 ? 'default' : Number(trunk.fail_rate || 0) <= 15 ? 'secondary' : 'destructive'}>
                            {Number(trunk.fail_rate || 0).toFixed(1)}%
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <span className={Number(trunk.asr || 0) >= 70 ? 'text-green-600' : Number(trunk.asr || 0) >= 40 ? 'text-yellow-600' : 'text-red-600'}>
                            {Number(trunk.asr || 0).toFixed(1)}%
                          </span>
                        </TableCell>
                        <TableCell className="text-right">{formatDuration(trunk.avg_duration || 0)}</TableCell>
                        <TableCell className="text-right">{Math.round(Number(trunk.total_duration || 0) / 60)}</TableCell>
                      </TableRow>
                    ))}
                    {trunksStats.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={11} className="text-center text-muted-foreground py-8">
                          Nessun dato disponibile per il periodo selezionato
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="cdr" className="space-y-6 mt-6">
          <CDRTable
            showFilters={false}
            showExport={false}
            compact={true}
            startDate={format(filters.startDate, 'yyyy-MM-dd')}
            endDate={format(filters.endDate, 'yyyy-MM-dd')}
            direction={filtersQuery.direction || 'all'}
            disposition={filtersQuery.disposition || 'all'}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
